import { createContext, useEffect, useState } from 'react';
import { Pago, MerchantPaymentResponse, ElectronicInvoiceResponse, InvoiceReport, ElectronicInvoiceDetailsResponse, Detail, InvoiceDetail, InvoiceProdByCategoryResponse, ProductCategory, Menu, ProductsResponse, MenusResponse, InvoiceByTariffResponse, Total } from '../../models/interfaces';
import topApi from '../../components/api/ToPApi';




type ReportsContextProps = {
    pagos: Pago[];
    invoices: InvoiceReport[];
    totalPage: number;
    isLoading: boolean;
    loadMerchantPaymentReport: (page: number, dateFrom: string, dateTo: string) => Promise<void>;
    getInvoiceByUserMerchant: (dateFrom: string, dateTo: string, invoiceId: string, type: string, consecutive: string) => Promise<void>;
    getInvoiceDetailById: (invoiceId: string, type: string) => Promise<InvoiceDetail[]>;
    getInvoiceProductByCategory: (dateFrom: string, dateTo: string, currency: number, top: number, menuId: number) => Promise<ProductCategory[]>;
    getMenusByMerchant: () => Promise<Menu[]>;
    getInvoiceTariff: (dateFrom: string, dateTo: string) => Promise<Total[]>;
    // Reporte de ventas (FC_SP_REPORTE_FACTURA / FC_SP_REPORTE_FACTURA_DETALLE)
    getSalesReport: (filters: SalesReportFilters) => Promise<{ invoices: Record<string, any>[]; country?: number }>;
    getSalesDetail: (invoiceId: number | string) => Promise<{ details: Record<string, any>[]; country?: number }>;
    getSalesPaymentTypes: () => Promise<{ paymentTypes: { code: string; description: string }[]; country?: number }>;


}



export type SalesReportFilters = {
    from: string;           // YYYYMMDD (inclusive)
    to: string;             // YYYYMMDD (inclusive)
    invoiceId?: string;     // si se indica, el SP ignora las fechas
    status?: number;        // -1 todos, 0 activa, 1 anulada, 2 pagada
    paymentType?: string;   // '' todos
};

// País del comercio en el reporte de ventas
export const SALES_COUNTRY_COSTA_RICA = 1;   // colones y dólares, con tipo de cambio
export const SALES_COUNTRY_PANAMA = 2;       // solo dólares

export const ReportsContext = createContext({} as ReportsContextProps);

export const ReportsProvider = ({ children }: any) => {

    const [pagos, setPagos] = useState<Pago[]>([]);
    const [invoices, setInvoices] = useState<InvoiceReport[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [totalPage, setTotalPage] = useState<number>(0);
    const pageSize = 25;



    const loadMerchantPaymentReport = async (pageActual, dateFrom, dateTo) => {

        const data = {
            "from": dateFrom,
            "to": dateTo,
            "page": pageActual,
            "pageSize": pageSize
        }

        const resp = await topApi.post<MerchantPaymentResponse>('/reports/admin/getMerchantPayments', data);
        if (pagos.length > 0) {
            setPagos(resp.data.pagos);
        }
        setPagos([...resp.data.pagos]);
        setTotalPage(resp.data.pages);

    }

    const getInvoiceByUserMerchant = async (dateFrom: string, dateTo: string, invoiceId: string, type: string, consecutive: string) => {

        const data = {
            "from": dateFrom,
            "to": dateTo,
            "invoiceId": invoiceId,
            "type": type,
            "consecutive": consecutive
        }

        const resp = await topApi.post<ElectronicInvoiceResponse>('/reports/electronic-invoice/getInvoiceByUser', data);
        if (invoices.length > 0) {
            setInvoices(resp.data.invoices);
        }
        setInvoices([...resp.data.invoices]);
    }

    const getInvoiceDetailById = async (invoiceId: string, type: string) => {

        const data = {
            "invoiceId": invoiceId,
            "type": type
        }

        const resp = await topApi.post<ElectronicInvoiceDetailsResponse>('/reports/electronic-invoice/getInvoiceDetailByUser', data);
        if (resp.status == 200) {
            return resp.data.details;
        }
        return []
    }

    const getInvoiceProductByCategory = async (dateFrom: string, dateTo: string, currency: number, top: number, menuId: number) => {
        setIsLoading(true);
        const data = {
            "from": dateFrom,
            "to": dateTo,
            "currency": currency,
            "top": top,
            "menuId": menuId
        }

        const resp = await topApi.post<InvoiceProdByCategoryResponse>('/reports/getInvoiceProductByCategory', data);
        setIsLoading(false);
        if (resp.status == 200) {
            return resp.data.report;
        }
        return ([]);

    }

    const getInvoiceTariff = async (dateFrom: string, dateTo: string) => {
        setIsLoading(true);
        const data = {
            "from": dateFrom,
            "to": dateTo
        }

        const resp = await topApi.post<InvoiceByTariffResponse>('/reports/getInvoiceTariff', data);
        setIsLoading(false);
        if (resp.status == 200) {
            return resp.data.report;
        }
        return ([]);

    }

    const getSalesReport = async (filters: SalesReportFilters) => {
        const resp = await topApi.post<{ invoices: Record<string, any>[]; country?: number }>('/reports/sales/getSalesByUser', filters);
        return { invoices: resp.data.invoices ?? [], country: resp.data.country };
    }

    const getSalesDetail = async (invoiceId: number | string) => {
        const resp = await topApi.post<{ details: Record<string, any>[]; country?: number }>('/reports/sales/getSalesDetailByUser', { invoiceId });
        return { details: resp.data.details ?? [], country: resp.data.country };
    }

    const getSalesPaymentTypes = async () => {
        try {
            const resp = await topApi.get<{ paymentTypes: { code: string; description: string }[]; country?: number }>('/reports/sales/getPaymentTypes');
            return { paymentTypes: resp.data.paymentTypes ?? [], country: resp.data.country };
        } catch (e) {
            return { paymentTypes: [] };
        }
    }

    //  /api/menu/getMenusByMerchant

    const getMenusByMerchant = async () => {

        const resp = await topApi.get<MenusResponse>('/menu/getMenusByMerchant');
        if (resp.status == 200) {
            return resp.data.menu;
        }
        return ([]);

    }



    return (
        <ReportsContext.Provider value={{
            pagos, totalPage, loadMerchantPaymentReport, getInvoiceByUserMerchant, invoices, getInvoiceDetailById,
            getInvoiceProductByCategory, getMenusByMerchant, isLoading, getInvoiceTariff,
            getSalesReport, getSalesDetail, getSalesPaymentTypes
        }}>
            {children}
        </ReportsContext.Provider>
    );
}